/**
 * Shared access model for Sites (internal/sites-access-model.md v2).
 * `access: public | registered | paid` lives on the **content item only** —
 * there is no site-level or category-level access any more. Paid items are
 * unlocked by paywall products whose `items` carry `item:<contentId>` refs,
 * or by `scope: 'site'` (all-access) products.
 */
import { periodSuffix } from '@/Utils/sitePeriod'

export const ACCESS_LEVELS = ['public', 'registered', 'paid']

export const ACCESS_LABELS = {
	public: 'Public',
	registered: 'Login required',
	paid: 'Paid',
}

export const ACCESS_DESCRIPTIONS = {
	public: 'Anyone can watch.',
	registered: 'Viewers must create a free account and sign in.',
	paid: 'Viewers need a plan or purchase that covers this.',
}

export const ACCESS_OPTIONS = ACCESS_LEVELS.map((value) => ({ value, text: ACCESS_LABELS[value] }))

export function normalizeAccess(value) {
	return ACCESS_LEVELS.includes(value) ? value : 'public'
}

export const itemRef = (contentId) => `item:${contentId}`

/**
 * A paid item must say what unlocks it: the owner either ticks `scope: items`
 * plans or explicitly chooses "only all-access plans unlock this". The
 * dashboard never assigns plans on its own.
 */
export function paidMappingChosen(access, planIds, allAccessOnly) {
	return access !== 'paid' || (planIds || []).length > 0 || allAccessOnly === true
}

/** `item:<id>` → `{ kind: 'item', id }`; legacy `channel:` / `category:` refs still parse so old data can be labelled. */
export function parseRef(ref) {
	const m = /^(item|category|channel):(.+)$/.exec(String(ref || ''))
	return m ? { kind: m[1] === 'channel' ? 'item' : m[1], id: m[2] } : null
}

/**
 * The mapping a `scope: items` plan ends up with when `refs` are added to it
 * (`selected`) or taken out of it (not selected). Order is kept so an
 * unchanged list compares equal.
 */
export function nextPlanItems(plan, refs, selected) {
	const list = Array.isArray(refs) ? refs : [refs]
	const items = plan?.items || []
	return selected ? [...items, ...list.filter((r) => !items.includes(r))] : items.filter((r) => !list.includes(r))
}

/** True when both lists hold the same refs (order aside). */
export function sameRefs(a, b) {
	const x = new Set(a || [])
	const y = new Set(b || [])
	return x.size === y.size && [...x].every((r) => y.has(r))
}

/**
 * Active `scope: items` plans that would be left unlocking nothing once `refs`
 * are mapped into exactly `selectedIds` — shown before saving, because a plan
 * without content is useless to viewers (the paywall used to refuse it, newer
 * castr answers `warning: plan_has_no_items`).
 */
export function plansEmptiedBy(products, refs, selectedIds) {
	const wanted = new Set(selectedIds || [])
	return (products || []).filter((p) => p.scope === 'items' && p.active !== false && (p.items || []).length > 0
		&& !wanted.has(p.public_id) && nextPlanItems(p, refs, false).length === 0)
}

/** Products whose mapping covers any of `refs` (all-access always does). */
export function productsCovering(products, refs) {
	const wanted = new Set(refs)
	return (products || []).filter((p) => {
		if (p.scope === 'site') return true
		return (p.items || []).some((ref) => wanted.has(ref))
	})
}

export function formatPrice(product) {
	if (!product) return ''
	if (product.free || Number(product.price) === 0) return 'Free'
	const amount = Number(product.price)
	const money = Number.isFinite(amount)
		? amount.toLocaleString(undefined, { style: 'currency', currency: (product.currency || 'USD').toUpperCase() })
		: `${product.price} ${product.currency || ''}`
	return product.recurring ? `${money} ${periodSuffix(product.period)}` : money
}

/**
 * Call-to-action a viewer sees for a plan — kept consistent across plan cards
 * and previews: recurring → "Subscribe", one-time → "Buy" (free → "Unlock").
 */
export function planCtaLabel(product) {
	if (!product) return ''
	if (product.recurring) return 'Subscribe'
	if (product.free || Number(product.price) === 0) return 'Unlock'
	return 'Buy'
}

export function planKindLabel(product) {
	if (product?.scope === 'site') return 'All access'
	const refs = (product?.items || []).map(parseRef).filter(Boolean)
	if (refs.length === 1) return 'Single item'
	return 'Bundle'
}
